import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, Save, RefreshCw, Shield, Rocket, Settings, Zap, Star, Euro, Clock, CheckCircle, X } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

// Icon mapping
const ICONS = {
  Shield: Shield,
  Rocket: Rocket,
  Settings: Settings,
  Zap: Zap,
  Star: Star
};

const ICON_OPTIONS = [
  { value: 'Shield', label: 'Bouclier', icon: Shield },
  { value: 'Rocket', label: 'Fusée', icon: Rocket },
  { value: 'Settings', label: 'Engrenage', icon: Settings },
  { value: 'Zap', label: 'Éclair', icon: Zap },
  { value: 'Star', label: 'Étoile', icon: Star }
];

const CATEGORY_OPTIONS = [
  { value: 'hebergement', label: 'Hébergement' },
  { value: 'maintenance', label: 'Maintenance' }
];

const CMSSubscriptionsPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, data: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try { 
      const res = await api.get('/cms/subscriptions'); 
      setSubscriptions(res.data); 
    }
    catch (error) { toast.error('Erreur de chargement'); } 
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    const { data } = editModal;
    if (!data.name || !data.price_monthly) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    try {
      if (data.id && !data.id.startsWith('new-')) { 
        await api.put(`/cms/subscriptions/${data.id}`, data); 
      } else { 
        const newData = {...data};
        delete newData.id;
        await api.post('/cms/subscriptions', newData); 
      }
      toast.success('Forfait enregistré'); 
      loadData(); 
      setEditModal({ open: false, data: null });
    } catch (error) { toast.error('Erreur lors de l\'enregistrement'); }
  };

  const handleDelete = async () => {
    try { 
      await api.delete(`/cms/subscriptions/${deleteModal.id}`); 
      toast.success('Forfait supprimé'); 
      loadData(); 
      setDeleteModal({ open: false, id: null }); 
    }
    catch (error) { toast.error('Erreur lors de la suppression'); }
  };

  const addFeature = () => {
    if (!newFeature.trim()) return;
    setEditModal({
      ...editModal, 
      data: {
        ...editModal.data, 
        features: [...(editModal.data.features || []), newFeature.trim()]
      }
    });
    setNewFeature('');
  };

  const removeFeature = (index) => {
    const features = [...editModal.data.features];
    features.splice(index, 1);
    setEditModal({...editModal, data: {...editModal.data, features}});
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Forfaits & Abonnements</h1>
          <p className="text-slate-500">Gérez les offres d'hébergement et de maintenance</p>
        </div>
        <Button 
          onClick={() => setEditModal({ 
            open: true, 
            data: { 
              id: `new-${Date.now()}`,
              is_active: true, 
              recommended: false,
              category: 'hebergement',
              icon: 'Shield',
              features: [],
              order: subscriptions.length + 1 
            } 
          })} 
          className="bg-gradient-to-r from-cyan-500 to-blue-600"
        >
          <Plus className="w-4 h-4 mr-2" />Nouveau forfait
        </Button>
      </div>

      {/* Category Sections */}
      {['hebergement', 'maintenance'].map(category => {
        const categoryItems = subscriptions.filter(s => s.category === category);
        if (categoryItems.length === 0) return null;
        
        return (
          <div key={category}>
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              {category === 'hebergement' ? <Shield className="w-5 h-5 text-emerald-500" /> : <Settings className="w-5 h-5 text-blue-500" />}
              {category === 'hebergement' ? 'Hébergement' : 'Maintenance'}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {categoryItems.map(sub => {
                const IconComponent = ICONS[sub.icon] || Shield;
                return (
                  <Card key={sub.id} className={cn(
                    "relative overflow-hidden transition-all hover:shadow-lg",
                    sub.recommended && "border-2 border-emerald-500"
                  )}>
                    {sub.recommended && (
                      <div className="absolute top-0 inset-x-0 py-1 text-center text-xs font-semibold text-white bg-emerald-500">
                        Recommandé
                      </div>
                    )}
                    <CardContent className={cn("pt-6", sub.recommended && "pt-10")}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                            <IconComponent className="w-5 h-5 text-slate-700" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800 text-sm">{sub.name}</h3>
                            <p className="text-xs text-slate-500">{sub.description}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold text-slate-800">{sub.price_monthly}€</span>
                          <span className="text-sm text-slate-500">/mois</span>
                        </div>
                        <p className="text-xs text-slate-400">ou {sub.price_yearly}€/an</p>
                      </div>
                      
                      <ul className="space-y-1 mb-4">
                        {(sub.features || []).slice(0, 3).map((feature, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                            <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span className="truncate">{feature}</span>
                          </li>
                        ))}
                        {(sub.features || []).length > 3 && (
                          <li className="text-xs text-slate-400 pl-4">
                            +{sub.features.length - 3} autres
                          </li>
                        )}
                      </ul>
                      
                      <div className="flex justify-end gap-1 pt-2 border-t">
                        <Button variant="ghost" size="sm" onClick={() => setEditModal({ open: true, data: sub })}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, id: sub.id })}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}

      {subscriptions.length === 0 && (
        <Card className="text-center py-12">
          <p className="text-slate-500 mb-4">Aucun forfait configuré</p>
          <Button 
            onClick={() => setEditModal({ 
              open: true, 
              data: { 
                id: `new-${Date.now()}`,
                is_active: true, 
                recommended: false,
                category: 'hebergement',
                icon: 'Shield',
                features: [],
                order: 1 
              } 
            })}
          >
            <Plus className="w-4 h-4 mr-2" />Créer le premier forfait
          </Button>
        </Card>
      )}

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, data: null })}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editModal.data?.id && !editModal.data?.id.startsWith('new-') ? 'Modifier' : 'Nouveau'} forfait
            </DialogTitle>
          </DialogHeader>
          {editModal.data && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom du forfait *</Label>
                  <Input 
                    value={editModal.data.name || ''} 
                    onChange={(e) => setEditModal({...editModal, data: {...editModal.data, name: e.target.value}})}
                    placeholder="Ex: Hébergement Business"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Catégorie *</Label>
                  <select 
                    className="w-full border rounded-md px-3 py-2 bg-white h-10"
                    value={editModal.data.category || 'hebergement'}
                    onChange={(e) => setEditModal({...editModal, data: {...editModal.data, category: e.target.value}})}
                  >
                    {CATEGORY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description courte</Label>
                <Input 
                  value={editModal.data.description || ''} 
                  onChange={(e) => setEditModal({...editModal, data: {...editModal.data, description: e.target.value}})}
                  placeholder="Ex: Pour les sites e-commerce"
                />
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Euro className="w-4 h-4" /> Prix mensuel *
                  </Label>
                  <Input 
                    type="number"
                    value={editModal.data.price_monthly || ''} 
                    onChange={(e) => setEditModal({...editModal, data: {...editModal.data, price_monthly: parseInt(e.target.value) || 0}})}
                    placeholder="Ex: 59"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Euro className="w-4 h-4" /> Prix annuel
                  </Label>
                  <Input 
                    type="number"
                    value={editModal.data.price_yearly || ''} 
                    onChange={(e) => setEditModal({...editModal, data: {...editModal.data, price_yearly: parseInt(e.target.value) || 0}})}
                    placeholder="Ex: 590"
                  />
                </div>
              </div>

              {/* Icon & Style */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Icône</Label>
                  <div className="flex gap-2">
                    {ICON_OPTIONS.map(opt => {
                      const Icon = opt.icon;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setEditModal({...editModal, data: {...editModal.data, icon: opt.value}})}
                          className={cn(
                            "w-10 h-10 rounded-lg flex items-center justify-center border-2 transition-all",
                            editModal.data.icon === opt.value 
                              ? "border-cyan-500 bg-cyan-50" 
                              : "border-slate-200 hover:border-slate-300"
                          )}
                          title={opt.label}
                        >
                          <Icon className="w-5 h-5" />
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Options</Label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <Switch 
                        checked={editModal.data.recommended || false}
                        onCheckedChange={(checked) => setEditModal({...editModal, data: {...editModal.data, recommended: checked}})}
                      />
                      Recommandé
                    </label>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-2">
                <Label>Caractéristiques</Label>
                <div className="space-y-2">
                  {(editModal.data.features || []).map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      <span className="flex-1 text-sm">{feature}</span>
                      <button 
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input 
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      placeholder="Ajouter une caractéristique..."
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    />
                    <Button type="button" variant="outline" onClick={addFeature}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Ordre d'affichage</Label>
                <Input 
                  type="number"
                  value={editModal.data.order || 1} 
                  onChange={(e) => setEditModal({...editModal, data: {...editModal.data, order: parseInt(e.target.value) || 1}})}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, data: null })}>
              Annuler
            </Button>
            <Button onClick={handleSave} className="bg-gradient-to-r from-cyan-500 to-blue-600">
              <Save className="w-4 h-4 mr-2" />Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, id: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer ce forfait ?</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 text-sm">Cette action est irréversible. Le forfait ne sera plus visible sur le site.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, id: null })}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSSubscriptionsPage;
